/** Licence counts for the Administration screen — **gate 15, decision P-06: measure, do not
 * enforce**.
 *
 * Members by role, and how many companies and sites. Counts of what is in use, read from routes
 * that already existed. **No price, no limit, no enforcement enters the product here or anywhere**,
 * and `Administration.test.tsx` asserts the absence on structure: the panel renders no control at
 * all, the row keys are a closed set, and the only requests it makes are the two reads.
 *
 * **Mounting.** `LicencePanel` is a panel, like `ApprovalsRoutePanel` and `LivenessPanel`, to be
 * rendered by `Settings.tsx` — which the orchestrator owns while this lands, so it is not wired
 * from here. The panel decides for itself what each role sees.
 *
 * **Three states, and a zero belongs to only one of them.** Each of the two sections reads on its
 * own, so one failing does not blank the other:
 *
 * - **failed** — `role="alert"`, the failure's own sentence and a retry. No count is printed, and
 *   in particular no `0`: a licence count of zero produced by a dropped connection is exactly the
 *   defect this product keeps shipping (`return data ?? []`, ten instances).
 * - **loading** — said, not blank.
 * - **measured** — the rows. A workspace with no companies yet really does have `0` of them, and
 *   that is rendered as a measured zero (`data-state="measured"`), which is a different DOM from
 *   the failure.
 *
 * **Admin only, as the server decides it.** `/members` is Admin-only; the other roles are told who
 * can see this rather than shown a panel that 403s (design rule 6).
 */

import { Loaded, ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { useMe } from "../api/auth";
import {
  LICENCE_AS_OF_NOW,
  LICENCE_MEASURES,
  ROLE_ORDER,
  sitesByCompany,
  tallyEstate,
  tallyMembers,
  todayUtc,
} from "../api/licences";
import { useEntities, useMembers } from "../api/queries";
import { BasisAndLimits } from "./ScreenParts";
import styles from "./Screen.module.css";
import own from "./Administration.module.css";

const ROLE_LABEL: Record<string, string> = {
  admin: "Admins",
  approver: "Approvers",
  contributor: "Contributors",
  auditor: "Auditors",
  viewer: "Viewers",
};

function Row({ rowKey, label, count, total }: { rowKey: string; label: string; count: number; total?: boolean }) {
  return (
    <li
      className={[own.row, total ? own.total : ""].filter(Boolean).join(" ")}
      data-licence-row={rowKey}
      data-count={count}
    >
      <span className={own.label}>{label}</span>
      <span className={own.count}>{count}</span>
    </li>
  );
}

function MembersByRole() {
  const members = useMembers(true);
  // Not `Loaded`: its empty branch would replace a measured result, and an Admin's own workspace
  // is never legitimately empty — if it were, the honest rendering is the zeros, not a sentence.
  if (members.error) {
    return <ReadFailed of="The member count" error={members.error} onRetry={() => void members.refetch()} />;
  }
  if (members.isPending || members.data === undefined) return <Empty>Loading…</Empty>;
  const tally = tallyMembers(members.data, todayUtc());
  // Known roles first, in privilege order; a role the server sent that we do not know follows.
  const roles = [
    ...ROLE_ORDER,
    ...Object.keys(tally.byRole)
      .filter((role) => !(ROLE_ORDER as readonly string[]).includes(role))
      .sort(),
  ];
  return (
    <div data-testid="licence-members" data-state="measured">
      <ul className={own.rows}>
        {roles.map((role) => (
          <Row key={role} rowKey={`role:${role}`} label={ROLE_LABEL[role] ?? role} count={tally.byRole[role] ?? 0} />
        ))}
        <Row rowKey="members-active" label="People who can sign in" count={tally.active} total />
        <Row rowKey="members-ended" label="Access ended (not counted)" count={tally.ended} />
        <Row rowKey="members-not-counted" label="Suspended, erased or access not started (not counted)" count={tally.notCounted} />
      </ul>
    </div>
  );
}

function Estate() {
  const entities = useEntities();
  if (entities.error) {
    return (
      <ReadFailed of="The company and site count" error={entities.error} onRetry={() => void entities.refetch()} />
    );
  }
  if (entities.isPending || entities.data === undefined) return <Empty>Loading…</Empty>;
  const tally = tallyEstate(entities.data);
  const companies = sitesByCompany(entities.data);
  return (
    <div data-testid="licence-estate" data-state="measured">
      <ul className={own.rows}>
        <Row rowKey="entities" label="Companies" count={tally.entities} />
        <Row rowKey="sites" label="Sites on record" count={tally.sites} total />
      </ul>
      {companies.length > 0 ? (
        <ul className={own.rows} data-testid="licence-by-company">
          {companies.map((company) => (
            <Row
              key={company.id}
              rowKey={`company-sites:${company.id}`}
              label={`${company.name} (${company.jurisdiction})`}
              count={company.sites}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function LicencePanel() {
  const me = useMe();
  return (
    <Panel title="Licence counts" sub="what is in use, counted" column="1 / 9">
      <Loaded query={me} of="Your sign-in" empty="Sign in to see how many people, companies and sites this workspace has.">
        {(person) =>
          person.role === "admin" ? (
            <div className={own.groups} data-testid="licence-counts">
              <section>
                <h3 className={own.group}>Members by role</h3>
                <MembersByRole />
              </section>
              <section>
                <h3 className={own.group}>Companies and sites</h3>
                <Estate />
              </section>
              {/* The long explanation, ranked rather than deleted: the counts are what an Admin came
                  for, and what a licence would be counted on is one click away. */}
              <BasisAndLimits lazy>
                <h3 className={own.group}>What a licence would be counted on</h3>
                <ul className={own.measures} data-testid="licence-measures">
                  {LICENCE_MEASURES.map((measure) => (
                    <li key={measure.key} data-measure={measure.key}>
                      <strong>{measure.label}.</strong> {measure.detail}
                    </li>
                  ))}
                </ul>
                <p>
                  These are counts of what this workspace uses. They are not a limit, and nothing
                  is enforced from them.
                </p>
                <p data-testid="licence-as-of-now">{LICENCE_AS_OF_NOW}</p>
              </BasisAndLimits>
            </div>
          ) : (
            <p className={styles.note} data-testid="licence-who-can">
              Only an <strong>Admin</strong> sees how many people, companies and sites this
              workspace has. Ask an Admin of this workspace.
            </p>
          )
        }
      </Loaded>
    </Panel>
  );
}
